import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Shield,
  Loader2,
  XCircle,
  Eye,
  EyeOff,
  Lock,
  Mail,
  User,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { vaultService } from '@/services/vault.service';
import { VaultInvitation, InvitationStatus } from '@/types/vault.types';
import { useRegisterFromInvitation } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/app';
import { RegisterFromInvitationData } from '@/types/auth.types';
// [AUTO-INIT VAULT 2026-05] Imports pour initialiser automatiquement le coffre
// après le register, sans demander de re-saisir le mot de passe.
import { initializeVault } from '@/utils/crypto/vaultCrypto';
import { useVaultKeyStore } from '@/store/vaultKeyStore';

// [SIMPLIFICATION UX 2026-05] Suppression du double champ "Confirmer le mot de passe"
// + simplification du message d'erreur du mot de passe.
// Le backend exige toujours `confirmPassword` dans le DTO, on le duplique au submit.
const registerSchema = z.object({
  email: z.string().email('Email invalide').min(1, 'Email requis'),
  password: z
    .string()
    .min(8, 'Mot de passe : au moins 8 caractères')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Le mot de passe doit contenir au moins 1 majuscule et 1 chiffre'
    ),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  acceptTerms: z.boolean().refine(val => val === true, {
    message: 'Vous devez accepter les conditions',
  }),
});

type FormData = z.infer<typeof registerSchema>;

export const RegisterFromInvitationPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const registerMutation = useRegisterFromInvitation();

  const [invitation, setInvitation] = useState<VaultInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const getUserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      acceptTerms: false,
    },
  });

  useEffect(() => {
    if (!token) {
      setError(t('vault.invitation.invalid.missingToken'));
      setLoading(false);
      return;
    }

    const fetchInvitation = async () => {
      try {
        const data = await vaultService.getInvitationByToken(token);
        setInvitation(data);

        // Pre-fill form with invitation data
        if (data.email) {
          setValue('email', data.email);
        }
        if (data.firstName) {
          setValue('firstName', data.firstName);
        }
        if (data.lastName) {
          setValue('lastName', data.lastName);
        }
      } catch (cause) {
        const err = cause as HttpApiError;
        console.error('Error fetching invitation:', err);
        setError(
          err.response?.data?.message ||
            t('vault.invitation.invalid.notFoundOrExpired')
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token, setValue, t]);

  const onSubmit = async (data: FormData) => {
    if (!token) return;

    try {
      const registrationData: RegisterFromInvitationData = {
        invitationToken: token,
        email: data.email,
        password: data.password,
        // [SIMPLIFICATION UX 2026-05] Le backend exige confirmPassword dans son DTO
        // mais l'utilisateur ne saisit qu'un seul mot de passe → on duplique.
        confirmPassword: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        preferredLanguage: 'fr',
        timezone: getUserTimezone(),
      };

      const response = await registerMutation.mutateAsync(registrationData);

      // [AUTO-INIT VAULT 2026-05] Si le coffre est en attente d'initialisation
      // (mode zero_knowledge), on le fait IMMÉDIATEMENT avec le mot de passe
      // qu'on a en mémoire. Plus besoin de demander à l'utilisateur de
      // re-saisir son mdp sur une page séparée — c'est ce qui bloquait des
      // salariés comme M. LOVISA (cas SAM'AUTO).
      if (response.data.vaultInitializationPending && response.data.vaultId) {
        try {
          const { masterKey, initData } = await initializeVault(data.password);
          await vaultService.initializeVaultKey(
            response.data.vaultId,
            initData
          );
          // Stocker la masterKey en mémoire (auto-lock 30 min) pour pouvoir
          // déchiffrer les documents immédiatement.
          useVaultKeyStore
            .getState()
            .unlockVault(response.data.vaultId, masterKey);
        } catch (initError) {
          // Si l'init échoue, on log et on redirige vers la page d'init
          // classique pour que l'utilisateur puisse réessayer manuellement.
          console.error(
            '[RegisterFromInvitation] Auto-init du coffre a échoué:',
            initError
          );
          navigate(`/vault/initialize/${response.data.vaultId}`);
          return;
        }
      }

      // Redirection finale
      if (response.data.requires2FASetup) {
        navigate('/setup-2fa');
      } else {
        navigate('/my-vaults');
      }
    } catch {
      // Error is handled by the mutation hook
    }
  };

  const isExpired = invitation
    ? new Date(invitation.expiresAt) < new Date()
    : false;

  // Loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="text-primary-600 h-10 w-10 animate-spin" />
          <p className="text-sm text-neutral-600">
            {t('vault.invitation.loading')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8">
          <div className="flex flex-col items-center text-center">
            <div className="bg-error-50 mb-4 flex h-12 w-12 items-center justify-center rounded-full">
              <XCircle className="text-error-600 h-6 w-6" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-neutral-900">
              {t('vault.invitation.invalid.title')}
            </h1>
            <p className="mb-6 text-sm text-neutral-600">
              {error || t('vault.invitation.invalid.description')}
            </p>
            <Link
              to={ROUTES.AUTH.LOGIN}
              className="bg-primary-600 hover:bg-primary-700 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors"
            >
              {t('vault.invitation.actions.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Invalid invitation status
  if (
    isExpired ||
    invitation.status === InvitationStatus.EXPIRED ||
    invitation.status === InvitationStatus.ACCEPTED ||
    invitation.status === InvitationStatus.DECLINED
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-neutral-900">
              {invitation.status === InvitationStatus.ACCEPTED
                ? t('vault.invitation.accepted.title')
                : invitation.status === InvitationStatus.DECLINED
                  ? t('vault.invitation.declined.title')
                  : t('vault.invitation.expired.title')}
            </h1>
            <p className="mb-6 text-sm text-neutral-600">
              {invitation.status === InvitationStatus.ACCEPTED
                ? t('vault.invitation.accepted.description')
                : invitation.status === InvitationStatus.DECLINED
                  ? t('vault.invitation.declined.description')
                  : t('vault.invitation.invalid.description')}
            </p>
            <Link
              to={ROUTES.AUTH.LOGIN}
              className="bg-primary-600 hover:bg-primary-700 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors"
            >
              {t('vault.invitation.auth.loginButton')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Valid invitation - show registration form
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
      <div className="w-full max-w-lg rounded-xl border border-neutral-200 bg-white">
        {/* Header */}
        <div className="border-b border-neutral-200 p-6">
          <div className="flex items-center gap-4">
            <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-lg">
              <Shield className="text-primary-600 h-6 w-6" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-neutral-900">
                {t('vault.register.title')}
              </h1>
              {invitation.vault?.name && (
                <p className="text-sm text-neutral-600">
                  {t('vault.register.vaultAccess')}{' '}
                  <span className="text-primary-600 font-medium">
                    {invitation.vault.name}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6">
          <div className="space-y-4">
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="mb-1 block text-xs font-medium tracking-wide text-neutral-500 uppercase"
                >
                  {t('vault.register.form.firstName')}
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <User className="h-4 w-4 text-neutral-400" />
                  </div>
                  <input
                    {...register('firstName')}
                    type="text"
                    id="firstName"
                    placeholder={t('vault.register.form.firstNamePlaceholder')}
                    className="focus:border-primary-500 focus:ring-primary-500 block w-full rounded-lg border border-neutral-300 py-2 pr-3 pl-9 text-sm focus:ring-1 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="mb-1 block text-xs font-medium tracking-wide text-neutral-500 uppercase"
                >
                  {t('vault.register.form.lastName')}
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <User className="h-4 w-4 text-neutral-400" />
                  </div>
                  <input
                    {...register('lastName')}
                    type="text"
                    id="lastName"
                    placeholder={t('vault.register.form.lastNamePlaceholder')}
                    className="focus:border-primary-500 focus:ring-primary-500 block w-full rounded-lg border border-neutral-300 py-2 pr-3 pl-9 text-sm focus:ring-1 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Email field */}
            <div>
              <label
                htmlFor="email"
                className="mb-1 block text-xs font-medium tracking-wide text-neutral-500 uppercase"
              >
                {t('vault.register.form.email')}
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Mail className="h-4 w-4 text-neutral-400" />
                </div>
                <input
                  {...register('email')}
                  type="email"
                  id="email"
                  readOnly
                  className="block w-full rounded-lg border border-neutral-200 bg-neutral-50 py-2 pr-3 pl-9 text-sm text-neutral-500"
                />
              </div>
              {errors.email && (
                <p className="text-error-600 mt-1 text-xs">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password field */}
            <div>
              <label
                htmlFor="password"
                className="mb-1 block text-xs font-medium tracking-wide text-neutral-500 uppercase"
              >
                {t('vault.register.form.password')}
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Lock className="h-4 w-4 text-neutral-400" />
                </div>
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder={t('vault.register.form.passwordPlaceholder')}
                  className="focus:border-primary-500 focus:ring-primary-500 block w-full rounded-lg border border-neutral-300 py-2 pr-9 pl-9 text-sm focus:ring-1 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-neutral-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-neutral-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-error-600 mt-1 text-xs">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* [SIMPLIFICATION UX 2026-05] Champ "Confirmer le mot de passe" supprimé.
                Un seul champ mot de passe avec bouton afficher/masquer. */}

            {/* Terms checkbox */}
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <div className="flex items-start space-x-3">
                <input
                  {...register('acceptTerms')}
                  type="checkbox"
                  id="acceptTerms"
                  className="text-primary-600 focus:ring-primary-500 mt-0.5 h-4 w-4 rounded border-neutral-300"
                />
                <div className="flex-1">
                  <label
                    htmlFor="acceptTerms"
                    className="text-xs leading-relaxed text-neutral-700"
                  >
                    {i18n.language.startsWith('fr') ? (
                      <>
                        J'accepte les{' '}
                        <Link
                          to={ROUTES.LEGAL.TERMS}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-500 font-medium underline"
                        >
                          conditions d'utilisation
                        </Link>{' '}
                        et la{' '}
                        <Link
                          to={ROUTES.LEGAL.PRIVACY}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-500 font-medium underline"
                        >
                          politique de confidentialité
                        </Link>
                      </>
                    ) : (
                      <>
                        I accept the{' '}
                        <Link
                          to={ROUTES.LEGAL.TERMS}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-500 font-medium underline"
                        >
                          terms of service
                        </Link>{' '}
                        and the{' '}
                        <Link
                          to={ROUTES.LEGAL.PRIVACY}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-500 font-medium underline"
                        >
                          privacy policy
                        </Link>
                      </>
                    )}
                  </label>
                </div>
              </div>
              {errors.acceptTerms && (
                <p className="text-error-600 mt-2 text-xs">
                  {errors.acceptTerms.message}
                </p>
              )}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting || registerMutation.isPending}
              className="bg-primary-600 hover:bg-primary-700 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-50"
            >
              {isSubmitting || registerMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('vault.register.form.submitting')}
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  {t('vault.register.form.submit')}
                </>
              )}
            </button>
          </div>

          {/* Already have account */}
          <div className="mt-4 text-center text-xs">
            <span className="text-neutral-600">
              {t('vault.register.alreadyHaveAccount')}
            </span>{' '}
            <Link
              to={`${ROUTES.AUTH.LOGIN}?redirect=/vault/invitation/${token}`}
              className="text-primary-600 hover:text-primary-500 font-medium"
            >
              {t('vault.register.login')}
            </Link>
          </div>
        </form>

        {/* Footer - Security note */}
        <div className="border-t border-neutral-200 bg-neutral-50 px-6 py-4">
          <div className="flex items-start gap-3 text-xs text-neutral-600">
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-neutral-400" />
            <p>{t('vault.invitation.security.message')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
